/**
 * A QUICK ADD'S PRESET ENTRIES (KPR-1053; epic KPE-027). A quick add's Fixed
 * default for a repeating group is a list of entries, headings included, that
 * a new item's form opens with — the fleet pre-trip inspection's checklist
 * under its section headings. keepr-api judges and stores it
 * (utils/quickAddTemplates/groupPresets.js, docs/SCHEMA.md "A repeating
 * group's preset entries"); this file is the editor's side of the wire. Pure.
 *
 *   - `presetGroup(group)`: the group as the quick add editor draws it — the
 *     item form's own entry editor (RepeatingGroupField), without its file
 *     columns: a file is attached by a person, never preset (the API refuses
 *     one).
 *   - `presetForWire(group, list)`: the template's list. Never an id or the
 *     form's `_key` (keepr gives each entry its id when the ITEM is saved);
 *     only the columns that hold something, and not what a new entry starts
 *     with anyway (a column's own default — today's date, say — is stamped on
 *     the item when it is made, never frozen into the template); never
 *     a column the person may only see, or one keepr works out. `{ value }`,
 *     or `{ error }` with the sentence the editor shows.
 *   - `presetSummary(group, list)`: the preview's line, "5 checks under 2
 *     headings".
 */

import { columnsOf } from '../config/repeatingGroups';
import { getElementInitialValue, isElementValueEmpty } from './elements';
import { countEntries, countPhrase, editableColumns, entriesOf, isHeading, sameColumnValue } from './repeatingGroupEntries';

const labelOf = element => element?.label?.singular || element?.name || '';

/** The group without its file columns: the editor never offers one. */
export function presetGroup(group) {
	const columns = columnsOf(group);
	if (!columns.some(column => column?.dataType === 'file')) return group;
	return { ...group, options: { ...group.options, columns: columns.filter(column => column?.dataType !== 'file') } };
}

/** The list as a quick add stores it, or why it cannot be saved yet. */
export function presetForWire(group, list, { zone } = {}) {
	const entries = entriesOf(list);
	if (!entries.length) return { error: `${labelOf(group)}: add an entry, or switch it to Skip.` };
	const columns = editableColumns(presetGroup(group)).filter(column => column?.dataType !== 'confirmations');
	// What a new entry starts with, in the collection's zone (newEntry's own rule).
	const starts = new Map(columns.map(column => [column.name, getElementInitialValue(column, { isNewRecord: true, zone })]));
	const value = [];
	for (const entry of entries) {
		if (isHeading(entry)) {
			const title = String(entry.section || '').trim();
			if (!title) return { error: `${labelOf(group)}: give each heading a title, or remove it.` };
			value.push({ section: title });
			continue;
		}
		const elements = {};
		columns.forEach((column) => {
			const held = entry?.elements?.[column.name];
			if (isElementValueEmpty(column, held) || sameColumnValue(held, starts.get(column.name))) return;
			elements[column.name] = held;
		});
		value.push({ elements });
	}
	return { value };
}

/** "3 checks", "5 checks under 2 headings". */
export function presetSummary(group, list) {
	const { real, headings } = countEntries(list);
	const entries = countPhrase(group, real);
	return headings ? `${entries} under ${headings} ${headings === 1 ? 'heading' : 'headings'}` : entries;
}
